import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker, Form, Grid, Input, Popconfirm, Radio, Select, message, theme } from 'antd';
import { Rule } from 'antd/es/form';
import { FieldData } from 'rc-field-form/es/interface';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useNavigate, useResolvedPath } from 'react-router-dom';
import { APOLLO_DATE_FORMAT, DATE_FORMAT, GENDERS, PhoneTypes } from '../../../../constants';
import {
  ApplicantContactDetailsContainerApplicantUserPhoneFieldsFragment,
  ApplicantUserPersonalInformation,
  ApplicantUserPersonalInformationInput,
  ApplicantUserPhoneInput
} from '../../../../generated';
import { Country } from '../../../../types/country';
import { CountryDropdownContainer } from '../../../shared/country-dropdown/country-dropdown-container';
import { StateSelect } from '../shared/components/state-select';
import { GenerationalSuffix, SelectOption } from './contact-details.container';
import { useBeforeUnload } from '../shared/components/custom-hooks';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

export interface ContactDetailsProps {
  data: ApplicantUserPersonalInformation;
  countryData: Country[];
  generationalSuffixOptions: GenerationalSuffix[];
  onSave: (values: ApplicantUserPersonalInformationInput) => void;
  applicantUserId: string;
}

const ErrorMessage = {
  AtLeast1Citizenship: 'Please add at least one citizenship'
};

export const ContactDetails: React.FC<ContactDetailsProps> = (props) => {
  const [isFocus, setIsFocus] = useState(false);
  const [isUpdatingApplicant, setIsUpdatingApplicant] = useState(false);
  const [selectablePhoneTypes, setSelectablePhoneTypes] = useState<SelectOption[]>(PhoneTypes);
  const [additionalPhoneNumbers, setAdditionalPhoneNumber] = useState<ApplicantContactDetailsContainerApplicantUserPhoneFieldsFragment[]>(
    props.data.contactInformation?.phone?.filter((phone): phone is ApplicantContactDetailsContainerApplicantUserPhoneFieldsFragment => phone !== null) ?? []
  );
  const [isLegalNameConsistsOfOneName, setIsLegalNameConsistsOfOneName] = useState<boolean>(props.data.identityDetails?.legalNameConsistsOfOneName ?? false);
  const [zipCodeRules, setZipCodeRules] = useState<Rule[]>([{ required: false, message: '' }]);

  const [form] = Form.useForm<ApplicantUserPersonalInformationInput>();
  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();

  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;
  const navigate = useNavigate();

  const resolvedPath = useResolvedPath('..').pathname;
  const selectedCountry = Form.useWatch(['contactInformation', 'address', 'country'], form);
  const selectedCitizenship = Form.useWatch<string[]>(['citizenship', 'currentCitizenOf'], form);

  useEffect(() => {
    if (additionalPhoneNumbers && additionalPhoneNumbers.length === 0) {
      form.setFieldsValue({ contactInformation: { phone: [{ isPrimary: true }] } }); // set the first phone number as primary by default
    }
  }, [additionalPhoneNumbers]);

  // ensure phone type dropdown closes when selected
  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const onPhoneSelectChanged = debounce((values: string, index: number) => {
    const newAdditionalPhone = [...additionalPhoneNumbers];
    newAdditionalPhone[index].phoneType = values;
    newAdditionalPhone[index].phoneNumber = '';

    const selectedPhoneTypes = newAdditionalPhone.map((phone) => phone.phoneType);

    const remainingPhoneTypes = selectablePhoneTypes.filter((type: SelectOption) => !selectedPhoneTypes.includes(type.value));
    setSelectablePhoneTypes(remainingPhoneTypes);
    setAdditionalPhoneNumber(newAdditionalPhone);
  }, 300);

  const onIsLegalNameConsistsOfOneNameChanged = (value: boolean) => {
    setIsLegalNameConsistsOfOneName(value);
  };

  const onCountryChange = (value: string) => {
    // Reset State/Province field when country changes
    form.setFieldsValue({
      contactInformation: {
        address: {
          stateOrProvince: undefined
        }
      }
    });

    switch (value) {
      case 'US':
      case 'CA':
        setZipCodeRules([
          { required: true, message: 'Please enter the ZIP/Postal code' },
          {
            pattern: /^\d{5}(?:[-\s]\d{4})?$/,
            message: 'Please enter a valid ZIP/Postal code'
          }
        ]);
        break;
      default:
        setZipCodeRules([{ required: false, message: '' }]);
        break;
    }
  };

  const onSaveProgress = async () => {
    clearValidationErrors();
    setIsFocus(false);
    setIsUpdatingApplicant(true);
    const values = form.getFieldsValue();
    if (values.contactInformation && values.identityDetails) {
      values.contactInformation.phone = values.contactInformation?.phone
        ?.filter((phone) => phone?.phoneNumber !== undefined)
        .map((phone) => {
          return {
            id: phone?.id,
            phoneType: phone?.phoneType,
            phoneNumber: phone?.phoneNumber,
            isPrimary: phone?.isPrimary
          };
        });

      values.identityDetails.dateOfBirth = values.identityDetails.dateOfBirth
        ? dayjs(values.identityDetails.dateOfBirth).utc().format(APOLLO_DATE_FORMAT).toUpperCase()
        : undefined;
      values.identityDetails.gender = values.identityDetails.gender ?? undefined;
      values.identityDetails.generationalSuffix = values.identityDetails.generationalSuffix ?? null;
      await props.onSave(values);
      setIsUpdatingApplicant(false);
    }
  };

  const onSubmit = () => {
    form
      .validateFields()
      .then(async (values) => {
        form.submit();
        setIsUpdatingApplicant(true);
        if (values.contactInformation && values.identityDetails) {
          values.contactInformation.phone = values.contactInformation.phone?.map((phone) => {
            return {
              id: phone?.id,
              phoneType: phone?.phoneType,
              phoneNumber: phone?.phoneNumber,
              isPrimary: phone?.isPrimary
            };
          });

          values.identityDetails.dateOfBirth = dayjs(values.identityDetails.dateOfBirth).utc().format(APOLLO_DATE_FORMAT);
          values.identityDetails.generationalSuffix = values.identityDetails.generationalSuffix ?? null;
          await props.onSave(values);
          setIsUpdatingApplicant(false);
          navigate(`/applicant/onboarding/primary-education-details`);
        }
      })
      .catch((error) => {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstErrorField = error.errorFields[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
        setIsUpdatingApplicant(false);
        console.log('Form Error', error);
      });
  };
  //block-if-not-saved
  useBeforeUnload(isFocus, 'Changes that you made may not be saved.');

  const formFocusHandler = () => {
    setIsFocus(true);
  };

  const clearValidationErrors = () => {
    const fields: FieldData[] = form.getFieldsError().map((field) => ({
      name: field.name,
      errors: []
    }));
    form.setFields(fields); // Clears validation errors but keeps current values
  };

  return (
    <>
      <div
        style={{
          background: colorBgContainer,
          minWidth: isMobile ? '100%' : '70%',
          padding: isMobile ? '24px' : '48px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          marginTop: '24px',
          width: '70%',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}
      >
        <h2>Identity Details</h2>
        <Form
          form={form}
          onFocus={formFocusHandler}
          layout="vertical"
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}
          initialValues={{ ...props.data }}
        >
          <Form.Item label="Last Name (Surname/Family Name)" rules={[{ required: true, message: 'Last Name is required' }]} name={['identityDetails', 'lastName']}>
            <Input
              maxLength={80}
              style={{ width: isMobile ? '119.5%' : 'auto' }}
              onChange={(e) => {
                if (e.target.value.length == 80) {
                  message.error('Last Name cannot exceed 80 characters.');
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name={['identityDetails', 'legalNameConsistsOfOneName']}
            rules={[
              {
                required: true,
                message: 'Legal Name Consists of One Name is required'
              }
            ]}
            label="Does your legal name consist of only one name?"
          >
            <Radio.Group
              onChange={(e) => {
                onIsLegalNameConsistsOfOneNameChanged(e.target.value);
              }}
            >
              <Radio value={false}>No</Radio>
              <Radio value={true}>Yes</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="Rest of Name"
            rules={[
              {
                required: !isLegalNameConsistsOfOneName,
                message: 'Rest of Name is required'
              }
            ]}
            name={['identityDetails', 'restOfName']}
            hidden={isLegalNameConsistsOfOneName}
          >
            <Input
              maxLength={80}
              style={{ width: isMobile ? '167%' : 'auto' }}
              onChange={(e) => {
                if (e.target.value.length == 80) {
                  message.error('Rest of Name cannot exceed 80 characters.');
                }
              }}
            />
          </Form.Item>
          <Form.Item label="Generational Suffix" name={['identityDetails', 'generationalSuffix']}>
            <Select
              allowClear={true}
              placeholder="Generational Suffix"
              options={props.generationalSuffixOptions}
              fieldNames={{ label: 'suffix', value: 'suffix' }}
              style={{ width: isMobile ? '180%' : 'auto' }}
            />
          </Form.Item>
          <div
            style={{
              display: 'flex',
              gap: '10px',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            <Form.Item name={['identityDetails', 'gender']} rules={[{ required: true, message: 'Gender is required' }]} label="Gender">
              <Select style={{ width: isMobile ? '100%' : 100 }} placeholder="Gender">
                {GENDERS.map((item) => {
                  return (
                    <Select.Option key={item.value} value={item.value} style={{ width: isMobile ? '100%' : 100, minHeight: '40px', padding: '8px' }}>
                      {item.label}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item
              name={['identityDetails', 'dateOfBirth']}
              valuePropName="value"
              rules={[{ required: true, message: 'Date of Birth is required' }]}
              label="Date of Birth"
              getValueProps={(value) => ({
                value: value ? dayjs(value).utc() : ''
              })}
            >
              <DatePicker
                maxDate={dayjs().utc().endOf('day')}
                value={props?.data?.identityDetails?.dateOfBirth ? dayjs(props?.data?.identityDetails?.dateOfBirth).utc().format(DATE_FORMAT).toUpperCase() : ''}
                format={(value) => value.format(DATE_FORMAT).toUpperCase()}
                style={{ width: isMobile ? '100%' : 'auto' }}
              />
            </Form.Item>
          </div>
          <h2>Contact Details</h2>
          <Form.Item rules={[{ required: true, message: 'Email is required', type: 'email' }]} name={['contactInformation', 'emailAddress']} label="Email Address">
            <Input placeholder="Email" maxLength={319} style={{ width: isMobile ? '165%' : 'auto' }} />
          </Form.Item>

          <p className="ant-form-item-label">Phone Numbers</p>
          <Form.List name={['contactInformation', 'phone']}>
            {(fields, { add, remove }) => {
              return (
                <div style={{}}>
                  {fields.map((field, index) => {
                    return (
                      <div key={field.key} style={{ marginBottom: '15px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', width: '100%' }}>
                        <div
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                            paddingRight: '10px'
                          }}
                        >
                          {field.key > 0 ? (
                            <MinusCircleOutlined
                              className="dynamic-delete-button"
                              onClick={() => {
                                const isPrimary = form.getFieldValue(['contactInformation', 'phone', index, 'isPrimary']);
                                console.log('isPrimary', isPrimary);
                                if (isPrimary) {
                                  form.setFieldValue(['contactInformation', 'phone', 0, 'isPrimary'], true);
                                }
                                remove(field.name);
                              }}
                            />
                          ) : null}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: '10px',
                            paddingTop: '3px',
                            paddingLeft: '10px',
                            borderLeft: '1px solid darkgrey',
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          <Form.Item name={[index, 'id']} hidden={true}>
                            <Input hidden={true} />
                          </Form.Item>
                          <Form.Item
                            name={[index, 'phoneType']}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                message: 'Phone Type is required'
                              }
                            ]}
                            label="Phone Type"
                          >
                            <Select
                              placeholder="Phone Type"
                              style={{ width: isMobile ? '157%' : 200 }}
                              onChange={(values) => {
                                onPhoneSelectChanged(values, index);
                              }}
                            >
                              {selectablePhoneTypes?.map((item: SelectOption) => (
                                <Select.Option key={item.value} value={item.value}>
                                  {item.label}
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          <Form.Item
                            name={[index, 'phoneNumber']}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[
                              {
                                required: true,
                                message: 'Phone Number is required'
                              }
                            ]}
                            label="Phone Number"
                          >
                            <Input placeholder="Phone Number" maxLength={15} style={{ width: isMobile ? '157%' : 'auto' }} />
                          </Form.Item>
                          <Form.Item name={[index, 'isPrimary']} valuePropName="checked">
                            <Radio
                              onChange={() => {
                                // unchecked all other radio buttons and set the current radio button to true
                                form.setFieldsValue({
                                  contactInformation: {
                                    phone: form.getFieldValue(['contactInformation', 'phone']).map((p: ApplicantUserPhoneInput, i: number) => {
                                      return {
                                        ...p,
                                        isPrimary: i === index
                                      };
                                    })
                                  }
                                });
                              }}
                              value="true"
                              style={{
                                marginTop: '-5px',
                                paddingTop: '40px',
                                textAlign: 'justify',
                                width: isMobile ? '100%' : 'auto'
                              }}
                            >
                              Primary
                            </Radio>
                          </Form.Item>
                        </div>
                      </div>
                    );
                  })}
                  {fields.length < 5 ? (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} style={{ width: isMobile ? '154%' : 395 }} block>
                        <PlusOutlined /> Add Additional Phone
                      </Button>
                    </Form.Item>
                  ) : null}
                </div>
              );
            }}
          </Form.List>
          <h2>Address of Residence</h2>
          <p className="text-xs mb-2">
            <span className="font-bold">Note:</span> You must enter the address where you currently reside.
          </p>
          <Form.Item rules={[{ required: true, message: 'Address Line 1 is required' }]} label="Address Line 1" name={['contactInformation', 'address', 'streetAddressLine1']}>
            <Input style={{ width: isMobile ? '165%' : 395 }} placeholder="Address Line 1" maxLength={64} />
          </Form.Item>
          <Form.Item label="Address Line 2" name={['contactInformation', 'address', 'streetAddressLine2']}>
            <Input style={{ width: isMobile ? '165%' : 395 }} placeholder="Address Line 2" maxLength={64} />
          </Form.Item>
          <CountryDropdownContainer
            name={['contactInformation', 'address', 'country']}
            rules={[{ required: true, message: 'Country is required' }]}
            label="Country"
            countryData={props.countryData}
            style={{ width: isMobile ? '195%' : 395 }}
            onChange={onCountryChange}
          />
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Form.Item label="City" name={['contactInformation', 'address', 'city']}>
              <Input placeholder="City" maxLength={50} style={{ flex: 1, minWidth: '150px' }} />
            </Form.Item>

            <Form.Item name={['contactInformation', 'address', 'stateOrProvince']}>
              <StateSelect name={['contactInformation', 'address', 'stateOrProvince']} selectedCountry={selectedCountry} countryData={props.countryData} />
            </Form.Item>

            <Form.Item label="Zip / Postal Code" rules={zipCodeRules} name={['contactInformation', 'address', 'zipOrPostalCode']}>
              <Input maxLength={10} placeholder="Zip / Postal Code" style={{ flex: 1, minWidth: '150px' }} />
            </Form.Item>
          </div>

          <h2>Citizenship Details</h2>
          <p className="ant-form-item-label">
            <span style={{ color: '#ff4d4f', fontFamily: 'SimSun,sans-serif' }}>*</span> Current Country of Citizenship
          </p>
          <Form.List
            name={['citizenship', 'currentCitizenOf']}
            rules={[
              {
                validator: async (_, values) => {
                  if (!values || values.length < 1) {
                    return Promise.reject(new Error(ErrorMessage.AtLeast1Citizenship));
                  }
                }
              }
            ]}
          >
            {(fields, { add, remove }, { errors }) => {
              return (
                <div>
                  {fields.map((field) => {
                    return (
                      <div key={field.key} style={{ marginBottom: '15px', width: isMobile ? '100%' : 'auto' }}>
                        <div
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'top',
                            paddingRight: '10px'
                          }}
                        >
                          <MinusCircleOutlined
                            className="dynamic-delete-button"
                            onClick={() => {
                              remove(field.name);
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'inline-block',
                            paddingTop: '3px',
                            paddingLeft: '10px',
                            borderLeft: '1px solid darkgrey',
                            width: isMobile ? '100%' : 'auto'
                          }}
                        >
                          <Form.Item
                            key={field.key}
                            name={[field.name]}
                            validateTrigger={['onChange', 'onBlur']}
                            rules={[{ required: true, message: 'Please select a field or delete this field.' }]}
                          >
                            <CountryDropdownContainer
                              isCitizenship={true}
                              placeholder="Country"
                              style={{ width: isMobile ? '132%' : 200 }}
                              showSearch
                              optionFilterProp="children"
                              labelRender={(labelInValue) => {
                                const country = labelInValue ? props.countryData.find((c) => c.countryCode === (labelInValue.value as string)) : null;
                                return country ? <span>{country.countryName}</span> : null;
                              }}
                              countryData={props.countryData}
                              selectedCitizenship={selectedCitizenship}
                              onChange={(value: string) => {
                                form.setFieldValue(['citizenship', 'currentCitizenOf', field.name], value);
                              }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    );
                  })}
                  {fields.length < 5 ? (
                    <Form.Item>
                      <Button
                        style={{ width: isMobile ? '130%' : 395 }}
                        type="dashed"
                        onClick={() => {
                          const currentCitizenOf = form.getFieldValue(['citizenship', 'currentCitizenOf']);
                          if (currentCitizenOf?.some((value: string) => value === undefined) ?? false) {
                            return;
                          }

                          add();
                        }}
                        block
                      >
                        <PlusOutlined /> Add Additional Citizenship
                      </Button>
                      <Form.ErrorList errors={errors} />
                    </Form.Item>
                  ) : null}
                </div>
              );
            }}
          </Form.List>
        </Form>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '24px 0px',
          gap: screens.xs ? '10px' : '0',
          width: screens.xs ? '100%' : '70%'
        }}
      >
        <div>
          <Popconfirm title={isFocus ? 'Are you sure? Changes you made will not be saved.' : null} onConfirm={() => navigate(resolvedPath)} disabled={!isFocus}>
            <Button onClick={() => (isFocus ? null : navigate(resolvedPath))} style={{ width: screens.xs ? '80px' : '90%', height: screens.xs ? '50px' : 'auto' }}>
              {'<< Back'}
            </Button>
          </Popconfirm>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.xs ? 'column' : 'row',
            gap: '15px'
          }}
        >
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={() => navigate(resolvedPath)}>
            <Button danger style={{ height: screens.xs ? '50px' : 'auto' }} disabled={isUpdatingApplicant}>
              Withdraw Request
            </Button>
          </Popconfirm>
          <Button onClick={onSaveProgress} style={{ height: screens.xs ? '50px' : 'auto' }} loading={isUpdatingApplicant}>
            Save Progress
          </Button>
          <Button type="primary" onClick={() => onSubmit()} style={{ height: screens.xs ? '50px' : 'auto' }} disabled={isUpdatingApplicant}>
            {'Save and Continue >>'}
          </Button>
        </div>
      </div>
    </>
  );
};
